import {
  type MaybeComputedElementRef,
  unrefElement,
  useEventListener,
  useTimeoutFn,
} from '@vueuse/core';
import {
  type MaybeRefOrGetter,
  computed,
  ref,
  toValue,
  useId,
  watch,
} from 'vue';

import { FOCUSABLE_SELECTOR } from '../shared/focusableSelector';

import { useMissingNameWarning } from './useMissingNameWarning';

type StartType = 'first' | 'last';

type ListboxNavigationOptionsType<T> = {
  isOpen: MaybeRefOrGetter<boolean>;
  options: MaybeRefOrGetter<readonly T[]>;
  getOptionId: (option: T) => string;
  /** The control that opens the popup and gets focus back when it closes. */
  trigger: MaybeComputedElementRef;
  /**
   * The popup root, including any search input or back button. Focus moving
   * outside it and the trigger closes the popup; focus left inside it is
   * returned to the trigger on close.
   */
  list: MaybeComputedElementRef;
  /** Id of the popup element. A generated one is used when omitted. */
  listId?: MaybeRefOrGetter<string | undefined>;
  popupRole?: 'listbox' | 'menu';
  isOptionDisabled?: (option: T) => boolean;
  isOptionSelected?: (option: T) => boolean;
  /** Enables first-letter type-ahead, for popups without a search input. */
  getOptionLabel?: (option: T) => string;
  /** ArrowDown on the last option goes to the first, and back. Clamps when false. */
  canWrap?: MaybeRefOrGetter<boolean>;
  /** Scrolls the active option into view. Needed when the list is virtualized. */
  scrollToOption?: (option: T, index: number) => void;
  /** Names the component in the missing accessible name warning. */
  componentName?: string;
  onSelect: (option: T, index: number) => void;
  /** Omit when the list is always shown, such as a list embedded in a popup another component owns. */
  onOpen?: () => void;
  /** Omit when another component owns the popup: Esc and focus leaving are then left to it. */
  onClose?: () => void;
  /** Runs before Esc closes the popup. Return true to keep it open, for example to leave a submenu. */
  onEscape?: () => boolean;
};

const TYPEAHEAD_RESET_MS = 500;
const TEXT_INPUT_TYPES = [
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
];
const OPEN_KEYS: Record<string, StartType> = {
  Enter: 'first',
  ' ': 'first',
  ArrowDown: 'first',
  ArrowUp: 'last',
};

function isTextEntry(target: EventTarget | null) {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.includes(target.type);
  }
  return target instanceof HTMLElement && target.isContentEditable;
}

function hasModifier(event: KeyboardEvent) {
  return event.altKey || event.ctrlKey || event.metaKey;
}

function focusTargetOf(element: Element | null | undefined) {
  if (!(element instanceof HTMLElement)) return null;
  if (element.tabIndex >= 0) return element;
  return element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? element;
}

function hasAccessibleName(element: HTMLElement | null) {
  if (!element) return true;
  return !!(
    element.getAttribute('aria-label')?.trim() ||
    element.getAttribute('aria-labelledby') ||
    element.getAttribute('title')?.trim() ||
    element.textContent?.trim() ||
    (element instanceof HTMLInputElement && element.labels?.length)
  );
}

/**
 * Keyboard navigation for a popup listbox or menu, using virtual focus: DOM
 * focus stays on the trigger (or a search input) and `activeId` goes on that
 * element's `aria-activedescendant`. Options are teleported to `<body>` and may
 * be virtualized, so moving real focus onto them would fight the overlay.
 *
 * The consumer owns the open state: `onOpen` and `onClose` ask it to change,
 * `isOpen` reports it. A list that is always shown inside a popup another
 * component owns passes `isOpen: true` and omits both, which leaves Esc and
 * focus leaving to that owner. Opening from the keyboard makes the selected option (or
 * the first, or the last for ArrowUp) active. Esc closes from anywhere while
 * open, and so does focus moving outside the trigger and the popup (Tab or
 * Shift+Tab out of either). Whenever the popup closes with focus inside it, or with focus lost to
 * `<body>`, focus goes back to the trigger; focus the user moved elsewhere is
 * left alone.
 *
 * The composable never changes the trigger's accessible name. Keep it stable
 * while opening and closing: VoiceOver drops pending announcements when the
 * focused control is renamed.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/ (2026-10-12)
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/ (2026-10-12)
 */
export function useListboxNavigation<T>({
  isOpen,
  options,
  getOptionId,
  trigger,
  list,
  listId,
  popupRole = 'listbox',
  isOptionDisabled,
  isOptionSelected,
  getOptionLabel,
  canWrap,
  scrollToOption,
  componentName = 'useListboxNavigation',
  onSelect,
  onOpen,
  onClose,
  onEscape,
}: ListboxNavigationOptionsType<T>) {
  const generatedListId = useId();
  const resolvedListId = computed(() => toValue(listId) ?? generatedListId);

  const activeOptionId = ref<string>();
  let pendingStart: StartType | null = null;
  let typeaheadBuffer = '';
  const { start: restartTypeaheadTimer, stop: stopTypeaheadTimer } =
    useTimeoutFn(
      () => {
        typeaheadBuffer = '';
      },
      TYPEAHEAD_RESET_MS,
      { immediate: false },
    );

  const enabledIndexes = computed(() =>
    toValue(options).flatMap((option, index) =>
      isOptionDisabled?.(option) ? [] : [index],
    ),
  );

  const activeIndex = computed(() => {
    if (!toValue(isOpen) || activeOptionId.value === undefined) return -1;
    return toValue(options).findIndex(
      (option) => getOptionId(option) === activeOptionId.value,
    );
  });

  const activeId = computed(() =>
    activeIndex.value < 0 ? undefined : activeOptionId.value,
  );

  const triggerAttrs = computed(() => {
    const isExpanded = toValue(isOpen);
    return {
      'aria-haspopup': popupRole,
      'aria-expanded': isExpanded ? 'true' : 'false',
      'aria-controls': isExpanded ? resolvedListId.value : undefined,
    } as const;
  });

  function setActiveIndex(index: number) {
    const option = toValue(options)[index];
    activeOptionId.value =
      option === undefined ? undefined : getOptionId(option);
  }

  function isActive(index: number) {
    return index === activeIndex.value;
  }

  function startIndex(start: StartType | null) {
    const optionList = toValue(options);
    const selected = enabledIndexes.value.find(
      (index) => !!isOptionSelected?.(optionList[index] as T),
    );
    if (selected !== undefined) return selected;
    if (start === 'first') return enabledIndexes.value[0] ?? -1;
    if (start === 'last') return enabledIndexes.value.at(-1) ?? -1;
    return -1;
  }

  function stepFrom(index: number, direction: 1 | -1) {
    const enabled = enabledIndexes.value;
    const first = enabled[0] ?? -1;
    const last = enabled.at(-1) ?? -1;
    if (index < 0) return direction > 0 ? first : last;
    const next =
      direction > 0
        ? enabled.find((candidate) => candidate > index)
        : enabled.findLast((candidate) => candidate < index);
    if (next !== undefined) return next;
    if (toValue(canWrap)) return direction > 0 ? first : last;
    return direction > 0 ? last : first;
  }

  function moveTo(event: KeyboardEvent, index: number) {
    event.preventDefault();
    if (index >= 0) setActiveIndex(index);
  }

  /** A held key only opens: its repeats would otherwise select at once. */
  function selectActive(event: KeyboardEvent) {
    if (event.repeat) {
      event.preventDefault();
      return;
    }
    const index = activeIndex.value;
    const option = toValue(options)[index];
    if (option === undefined || isOptionDisabled?.(option)) return;
    event.preventDefault();
    onSelect(option, index);
  }

  function resetTypeahead() {
    typeaheadBuffer = '';
    stopTypeaheadTimer();
  }

  /** A label continues with a space, so Space after letters keeps searching rather than selecting. */
  function isTypeaheadContinuedBySpace() {
    if (!typeaheadBuffer || !getOptionLabel) return false;
    const search = `${typeaheadBuffer} `;
    const optionList = toValue(options);
    return enabledIndexes.value.some((index) =>
      getOptionLabel(optionList[index] as T)
        .trim()
        .toLowerCase()
        .startsWith(search),
    );
  }

  function typeahead(event: KeyboardEvent) {
    if (!getOptionLabel || event.key.length !== 1) return;
    if (isTextEntry(event.target)) return;
    event.preventDefault();
    restartTypeaheadTimer();
    typeaheadBuffer += event.key.toLowerCase();

    const isRepeatedKey = [...typeaheadBuffer].every(
      (character) => character === typeaheadBuffer[0],
    );
    const search = isRepeatedKey ? event.key.toLowerCase() : typeaheadBuffer;
    const enabled = enabledIndexes.value;
    const position = enabled.indexOf(activeIndex.value);
    const offset = isRepeatedKey ? position + 1 : Math.max(position, 0);
    const optionList = toValue(options);
    const match = [...enabled.slice(offset), ...enabled.slice(0, offset)].find(
      (index) =>
        getOptionLabel(optionList[index] as T)
          .trim()
          .toLowerCase()
          .startsWith(search),
    );
    if (match !== undefined) setActiveIndex(match);
  }

  /**
   * Another control inside the popup, such as a checkbox or a back button,
   * keeps Enter and Space for itself. Only the trigger and the element that
   * points aria-activedescendant at the options select with them.
   */
  function isOtherControl(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) return false;
    if (target.hasAttribute('aria-activedescendant')) return false;
    if (unrefElement(trigger)?.contains(target)) return false;
    return target.matches(FOCUSABLE_SELECTOR);
  }

  /**
   * Arrow keys, Home, End, Enter and Space on whichever element holds focus
   * while the popup is open. In a search input, Space types, and Home and End
   * move the caret until an option is active.
   */
  function onListKeydown(event: KeyboardEvent) {
    if (event.isComposing || hasModifier(event)) return;
    const isTyping = isTextEntry(event.target);
    const isActivatingAnotherControl =
      (event.key === 'Enter' || event.key === ' ') &&
      isOtherControl(event.target);
    if (isActivatingAnotherControl) return;
    const hasActiveOption = activeIndex.value >= 0;
    switch (event.key) {
      case 'ArrowDown':
        return moveTo(event, stepFrom(activeIndex.value, 1));
      case 'ArrowUp':
        return moveTo(event, stepFrom(activeIndex.value, -1));
      case 'Home':
        if (isTyping && !hasActiveOption) return;
        return moveTo(event, enabledIndexes.value[0] ?? -1);
      case 'End':
        if (isTyping && !hasActiveOption) return;
        return moveTo(event, enabledIndexes.value.at(-1) ?? -1);
      case 'Enter':
        return selectActive(event);
      case ' ':
        if (isTyping) return;
        if (isTypeaheadContinuedBySpace()) return typeahead(event);
        return selectActive(event);
      default:
        return typeahead(event);
    }
  }

  /**
   * Opens on Enter, Space, ArrowDown or ArrowUp. While open it navigates like
   * `onListKeydown`.
   */
  function onTriggerKeydown(event: KeyboardEvent) {
    if (toValue(isOpen)) {
      onListKeydown(event);
      return;
    }
    if (event.isComposing || hasModifier(event)) return;
    const start = OPEN_KEYS[event.key];
    if (!start || (event.key === ' ' && isTextEntry(event.target))) return;
    event.preventDefault();
    pendingStart = start;
    onOpen?.();
  }

  function isFocusInPopupOrLost() {
    const focused = document.activeElement;
    if (!focused || focused === document.body) return true;
    return !!unrefElement(list)?.contains(focused);
  }

  watch(
    () => toValue(isOpen),
    (isNowOpen) => {
      if (isNowOpen) {
        setActiveIndex(startIndex(pendingStart));
        pendingStart = null;
        return;
      }
      const shouldReturnFocus = isFocusInPopupOrLost();
      activeOptionId.value = undefined;
      pendingStart = null;
      resetTypeahead();
      if (shouldReturnFocus) focusTargetOf(unrefElement(trigger))?.focus();
    },
  );

  watch(
    activeIndex,
    (index) => {
      const option = toValue(options)[index];
      if (option === undefined) return;
      if (scrollToOption) {
        scrollToOption(option, index);
        return;
      }
      document
        .getElementById(getOptionId(option))
        ?.scrollIntoView?.({ block: 'nearest' });
    },
    { flush: 'post' },
  );

  function documentWhileDismissible() {
    return onClose && toValue(isOpen) ? document : undefined;
  }

  useEventListener(
    documentWhileDismissible,
    'keydown',
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      if (onEscape?.()) return;
      onClose?.();
    },
    { capture: true },
  );

  useEventListener(
    documentWhileDismissible,
    'focusin',
    (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (unrefElement(trigger)?.contains(target)) return;
      if (unrefElement(list)?.contains(target)) return;
      onClose?.();
    },
    { capture: true },
  );

  useMissingNameWarning({
    isNamed: () => hasAccessibleName(focusTargetOf(unrefElement(trigger))),
    component: componentName,
    role: popupRole === 'menu' ? 'menu button' : 'combobox',
    hint: 'give the trigger visible text, aria-label or aria-labelledby',
  });

  return {
    listId: resolvedListId,
    activeId,
    activeIndex,
    triggerAttrs,
    onTriggerKeydown,
    onListKeydown,
    isActive,
    setActiveIndex,
  };
}
